import React, { useState, useCallback, useRef } from "react";
import ConfirmBeforeActionDialog from "./dialog-confirm-before-action";

export const useConfirmAction = (handler, dialogOptions = {}) => {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [data, setData] = useState([]);

  // Keep the latest handler, options, and the pending promise resolver in refs.
  // This keeps handleProceed / handleCancel / ConfirmDialogElement at STABLE
  // identities across UNRELATED parent re-renders (e.g. a snackbar/toast
  // appearing or disappearing). Previously dialogOptions was a fresh object every
  // render and sat in ConfirmDialogElement's deps, so the element identity
  // changed every render — React then REMOUNTED the dialog mid-interaction,
  // wiping the user's typed "delete" confirmation while deleting cases back to
  // back. Reading options live from a ref decouples the dialog from those
  // re-renders; it only remounts when its OWN state (open/data) actually changes.
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const optionsRef = useRef(dialogOptions);
  optionsRef.current = dialogOptions;
  const resolveRef = useRef(null);

  const handleConfirmAction = useCallback((d) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setData(d);
      setDialogOpen(true);
    }).then((confirmed) => {
      if (confirmed) {
        handlerRef.current(d);
      }
    });
  }, []);

  const handleProceed = useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current(true);
      resolveRef.current = null;
    }
    setDialogOpen(false);
  }, []);

  const handleCancel = useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
    setDialogOpen(false);
  }, []);

  // Depends ONLY on the dialog's own state (isDialogOpen, data) + stable
  // callbacks — never on the unstable dialogOptions object — so it does not
  // remount on unrelated re-renders. Option values are read live from the ref.
  const ConfirmDialogElement = useCallback(
    () => {
      const opts = optionsRef.current || {};
      return (
        <ConfirmBeforeActionDialog
          open={isDialogOpen}
          onProceed={handleProceed}
          onCancel={handleCancel}
          title={opts.title || "Confirm Action"}
          confirmMessage={opts.confirmMessage || "Are you sure you want to proceed?"}
          proceedButtonLabel={opts.proceedButtonLabel || "Confirm"}
          cancelButtonLabel={opts.cancelButtonLabel || "Cancel"}
          confirmKeyword={opts.confirmKeyword} // require typing this word (e.g. "delete") before Proceed
          destructive={opts.destructive}
          entityNamePrefix={opts.entityNamePrefix} // e.g. "You are about to delete the following case:"
          getEntityName={opts.getEntityName} // (data) => string shown in bold (e.g. the case name)
          list_element={opts.list_element || (() => <></>)} // Provide an empty element if not specified
          data={data || []}
        />
      );
    },
    [isDialogOpen, data, handleProceed, handleCancel]
  );

  return { handleConfirmAction, ConfirmDialogElement };
};
